import React, { useState } from 'react';
import { formatBalanceNav } from '../../data/countries';

const INVEST_SUB_ITEMS = [
  { id: "home", label: "Browse listings", icon: "home", iconColor: "#0b1f2a", iconBg: "rgba(212, 176, 111, 0.35)" },
  { id: "funded-properties", label: "Funded properties", icon: "check-circle", iconColor: "#1a5f3f", iconBg: "rgba(26, 95, 63, 0.12)" },
  { id: "marketplace", label: "Marketplace", icon: "store", iconColor: "#17a2b8", iconBg: "rgba(23, 162, 184, 0.14)" },
  { id: "calculator", label: "Calculator", icon: "calculator", iconColor: "#64748b", iconBg: "rgba(100, 116, 139, 0.12)" },
  { id: "news", label: "Insights", icon: "newspaper", iconColor: "#18384a", iconBg: "rgba(24, 56, 74, 0.1)" },
];

const PROFILE_SUB_ITEMS = [
  { id: "profile", label: "Account", icon: "user-circle", iconColor: "#102734", iconBg: "rgba(16, 39, 52, 0.1)" },
  { id: "dashboard", label: "Portfolio", icon: "chart-pie", iconColor: "#1a5f3f", iconBg: "rgba(26, 95, 63, 0.12)" },
  { id: "favorites", label: "Favorites", icon: "heart", iconColor: "#c73e55", iconBg: "rgba(220, 53, 69, 0.12)", badgeKey: "favorites" },
  { id: "messages", label: "Messages", icon: "comments", iconColor: "#0d9488", iconBg: "rgba(13, 148, 136, 0.12)", badgeKey: "messages" },
  { id: "agent-dashboard", label: "My Listings", icon: "clipboard-list", iconColor: "#bf9a52", iconBg: "rgba(212, 176, 111, 0.2)" },
];

const TOP_LEVEL_ITEMS = [
  { id: "rent", label: "Rent", icon: "key", iconColor: "#0d9488", iconBg: "rgba(13, 148, 136, 0.12)" },
  { id: "list-property", label: "List property", icon: "plus-circle", iconColor: "#102734", iconBg: "rgba(16, 39, 52, 0.1)" },
  { type: "group", id: "invest", label: "Invest", icon: "chart-line", iconColor: "#0b1f2a", iconBg: "rgba(212, 176, 111, 0.35)", children: INVEST_SUB_ITEMS },
  { type: "group", id: "profile-menu", label: "Profile", icon: "user", iconColor: "#102734", iconBg: "rgba(16, 39, 52, 0.1)", children: PROFILE_SUB_ITEMS, loggedInOnly: true },
];

const Navbar = ({ page, setPage, balance, isLoggedIn, isAdmin, onLogout, country, onAddFunds, favoritesCount = 0, messagesUnreadCount = 0 }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({ invest: false, "profile-menu": false });

  const badges = { favorites: favoritesCount, messages: messagesUnreadCount };

  const investPageIds = INVEST_SUB_ITEMS.map((item) => item.id);
  const profilePageIds = PROFILE_SUB_ITEMS.map((item) => item.id);
  const allPageIds = [...investPageIds, ...profilePageIds, "rent", "list-property"];
  const menuActive = allPageIds.includes(page) || page === "deposit";

  const navigate = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const isGroupActive = (item) => item.type === "group" && item.children.some((child) => child.id === page);

  const isGroupExpanded = (item) => {
    if (item.type !== "group") return false;
    if (Object.prototype.hasOwnProperty.call(expandedGroups, item.id)) {
      return expandedGroups[item.id];
    }
    return isGroupActive(item);
  };

  const toggleGroup = (item) => {
    const currentlyExpanded = isGroupExpanded(item);
    setExpandedGroups((prev) => ({ ...prev, [item.id]: !currentlyExpanded }));
  };

  const renderNavIcon = (icon, iconColor, iconBg, badge = 0) => (
    <span className="mobile-nav-icon-wrap">
      <i className={`fas fa-${icon} mobile-nav-icon`} style={{ color: iconColor, background: iconBg }} aria-hidden="true"></i>
      {badge > 0 ? (
        <span className="mobile-nav-badge" aria-hidden="true">{badge > 99 ? "99+" : badge}</span>
      ) : null}
    </span>
  );

  const visibleTopItems = [
    ...TOP_LEVEL_ITEMS.filter((item) => !item.loggedInOnly || isLoggedIn),
    ...(!isLoggedIn ? [{
      id: "favorites",
      label: "Favorites",
      icon: "heart",
      iconColor: "#c73e55",
      iconBg: "rgba(220, 53, 69, 0.12)",
      badgeKey: "favorites",
    }] : []),
  ];

  return (
    <nav className="navbar">
      <div className={`nav-container${isLoggedIn ? "" : " nav-container--guest"}`}>
        <div className="nav-left-cluster">
          <button
            type="button"
            className={`mobile-menu-btn${menuActive && !mobileOpen ? " active" : ""}`}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`fas fa-${mobileOpen ? "times" : "bars"}`}></i>
          </button>
          <a
            className={`nav-pill-link ${page === "rent" ? "active" : ""}`}
            onClick={() => setPage("rent")}
            title="Browse rental listings"
          >
            Rent
          </a>
          <a className={`nav-pill-link ${page === "list-property" ? "active" : ""}`} onClick={() => setPage("list-property")} title="List your property">
            List
          </a>
        </div>
        <div className="logo nav-logo-center brand-logo" onClick={() => setPage("home")}>
          <img src="/coown-logo-mark.svg" alt="" className="brand-logo-mark" width="40" height="40" />
          <span className="brand-logo-name">CoOwn</span>
        </div>
        <div className="nav-actions">
          {isLoggedIn ? (
            <>
              <div className="nav-wallet-chip">
                <span className="nav-wallet-chip__balance" title={`$${balance.toLocaleString()} USD`}>
                  <i className="fas fa-wallet nav-wallet-chip__icon" aria-hidden="true"></i>
                  {formatBalanceNav(balance, country)}
                </span>
                <button type="button" className="nav-wallet-chip__add" onClick={onAddFunds} aria-label="Add funds">
                  <i className="fas fa-plus" aria-hidden="true"></i> Add
                </button>
              </div>
              <button type="button" className="btn btn-ghost btn-sm nav-logout-btn" onClick={onLogout} aria-label="Log out">
                <i className="fas fa-sign-out-alt"></i>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-outline btn-sm btn-auth nav-auth-login" onClick={() => setPage("login")} aria-label="Log in">
                <i className="fas fa-sign-in-alt nav-auth-login-icon" aria-hidden="true"></i>
                <span className="nav-auth-login-label">Log In</span>
              </button>
              <button type="button" className="btn btn-primary btn-sm btn-auth nav-auth-signup" onClick={() => setPage("register")}>
                <span className="nav-auth-signup-label nav-auth-signup-label--long">Sign Up</span>
                <span className="nav-auth-signup-label nav-auth-signup-label--short" aria-hidden="true">Join</span>
              </button>
            </>
          )}
        </div>
      </div>
      <div className={`mobile-nav-backdrop ${mobileOpen ? "open" : ""}`} onClick={() => setMobileOpen(false)} aria-hidden={!mobileOpen} />
      <div className={`mobile-nav-drawer ${mobileOpen ? "open" : ""}`} role="navigation" aria-hidden={!mobileOpen}>
        <div className="mobile-nav-drawer-header">
          <span className="mobile-nav-drawer-title">Menu</span>
          <button type="button" className="mobile-menu-btn" aria-label="Close menu" onClick={() => setMobileOpen(false)}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        {visibleTopItems.map((item) => {
          if (item.type === "group") {
            const expanded = isGroupExpanded(item);
            return (
              <div key={item.id} className="mobile-nav-group">
                <button
                  type="button"
                  className={`mobile-nav-group-toggle${isGroupActive(item) ? " active" : ""}`}
                  aria-expanded={expanded}
                  onClick={() => toggleGroup(item)}
                >
                  {renderNavIcon(item.icon, item.iconColor, item.iconBg)}
                  <span className="mobile-nav-group-label">{item.label}</span>
                  <i className={`fas fa-chevron-${expanded ? "up" : "down"} mobile-nav-group-chevron`} aria-hidden="true"></i>
                </button>
                {expanded ? (
                  <div className="mobile-nav-sublist">
                    {item.children.map((child) => {
                      const badge = child.badgeKey ? badges[child.badgeKey] || 0 : 0;
                      return (
                        <a
                          key={child.id}
                          className={`mobile-nav-subitem${page === child.id ? " active" : ""}`}
                          aria-label={badge > 0 ? `${child.label}, ${badge}` : child.label}
                          onClick={() => navigate(child.id)}
                        >
                          {renderNavIcon(child.icon, child.iconColor, child.iconBg, badge)}
                          {child.label}
                        </a>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }

          const badge = item.badgeKey ? badges[item.badgeKey] || 0 : 0;
          return (
            <a
              key={item.id}
              className={page === item.id ? "active" : ""}
              aria-label={badge > 0 ? `${item.label}, ${badge}` : item.label}
              onClick={() => navigate(item.id)}
            >
              {renderNavIcon(item.icon, item.iconColor, item.iconBg, badge)}
              {item.label}
            </a>
          );
        })}

        {isLoggedIn ? (
          <>
            <div className="mobile-nav-drawer-balance">
              Balance: {formatBalanceNav(balance, country)}
            </div>
            {isAdmin ? (
              <a className={page === "admin" ? "active" : ""} onClick={() => navigate("admin")}>
                {renderNavIcon("shield-alt", "#102734", "rgba(16, 39, 52, 0.1)")}
                Admin dashboard
              </a>
            ) : null}
            <a onClick={() => { onAddFunds(); setMobileOpen(false); }} style={{ marginTop: "0.25rem" }}>
              {renderNavIcon("plus", "#0b1f2a", "rgba(212, 176, 111, 0.35)")}
              Add Funds
            </a>
            <a onClick={() => { onLogout(); setMobileOpen(false); }} style={{ marginTop: "0.25rem", color: "var(--danger)" }}>
              {renderNavIcon("sign-out-alt", "#dc3545", "rgba(220, 53, 69, 0.1)")}
              Log Out
            </a>
          </>
        ) : (
          <div style={{ display: "flex", gap: "0.5rem", padding: "1rem 0.5rem 0", marginTop: "0.5rem", borderTop: "1px solid var(--border)" }}>
            <button className="btn btn-outline btn-sm btn-auth" style={{ flex: 1 }} onClick={() => { setPage("login"); setMobileOpen(false); }}>Log In</button>
            <button className="btn btn-primary btn-sm btn-auth" style={{ flex: 1 }} onClick={() => { setPage("register"); setMobileOpen(false); }}>Sign Up</button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
